/* ═══════════════════════════════════════════════════════
   NYC Suite — Calendar Sync Orchestrator
   ═══════════════════════════════════════════════════════
   Diffs prev vs curr job, dispatches create/update/delete event ops,
   returns updated sessions with new gcalEventIds + fingerprints.
*/
import { sessionToEvent, sendUpdatesFor, eventCrewCall, startLineLabel, isHHmm, EVENT_CREATED, EVENT_DELETED } from "./calendarEvents.js";
import { normalizeCrew, isHydratedSessionId, migrateJob } from "./utils.js";

/* The schedule the calendar was last given for a session: `date|call|wrap`,
   and, only when the card's crew call moves the event (eventCrewCall), the
   start it was given and the wording of the line under that start,
   `date|call|wrap|crewCall|startLabel` ("Talent arrives" / "Start", from the
   crew card's subject: startLineLabel). Nothing is added when nothing is
   moved, so every fingerprint written before crew calls existed still reads
   the same. The fingerprint is written back to the session only after a
   successful create or update (DataContext.persistSessionSyncResult) and
   reaches every tab through the jobs listener: it is the persisted record of
   what the calendar has, and every sync diffs against it. */
export const computeFingerprint = (session, crewCall = "", crewSubject = "") => {
  const base = `${session?.date || ""}|${session?.callTime || ""}|${session?.wrapTime || ""}`;
  const moved = eventCrewCall(session, crewCall);
  return moved ? `${base}|${moved}|${startLineLabel(crewSubject)}` : base;
};

/* What the calendar was last given for a session, read back from its
   fingerprint: the schedule (`date`, `callTime`, `wrapTime`: the first three
   parts), `crewCall`, the start its event was given ("" when the event
   starts at the session call), and `startLabel`, the wording of the line
   under that start ("" when there is no moved start, or the fingerprint
   predates the wording part, so the next sync refreshes it silently). All ""
   for a session with no fingerprint (never synced, or marked for reconcile
   (null), which re-pushes whole) or one without the three schedule parts.
   Nothing else reads the fingerprint's parts. */
const notSynced = () => ({ date: "", callTime: "", wrapTime: "", crewCall: "", startLabel: "" });
export const syncedCalendarStateOf = (session) => {
  const fp = session?.gcalSyncedFingerprint;
  if (typeof fp !== "string") return notSynced();
  const parts = fp.split("|");
  if (parts.length < 3) return notSynced();
  const crewCall = isHHmm(parts[3]) ? parts[3] : "";
  return { date: parts[0], callTime: parts[1], wrapTime: parts[2], crewCall, startLabel: crewCall ? (parts[4] || "") : "" };
};

/* After a crew-card write landed: does the calendar differ from the committed
   cards for any session of the job? `committed` is the job's committed maps
   ({crewCalls, crewSubjects} by session id, DataContext.committedCalendarFor);
   the sessions are the ones the Suite hydrates (migrateJob), keyed as the
   cards are. A session differs when the start the committed crew call gives
   its event (eventCrewCall) is not the start its fingerprint records, or,
   under a moved start, when the wording the committed subject gives the line
   is not the wording recorded. A session with no fingerprint (never synced,
   or marked for reconcile) differs only when a crew call would move it: with
   none, the next job save creates or re-pushes its event, as before. */
export const calendarNeedsSync = (job, committed = {}) => {
  if (!job) return false;
  const crewCalls = committed.crewCalls || {};
  const crewSubjects = committed.crewSubjects || {};
  return (migrateJob(job).sessions || []).some(s => {
    if (!s?.date) return false;
    const synced = syncedCalendarStateOf(s);
    const moved = eventCrewCall(s, crewCalls[s.id] || "");
    return moved !== synced.crewCall || (!!moved && startLineLabel(crewSubjects[s.id] || "") !== synced.startLabel);
  });
};

/* A job written outside the browser (tools/crm/fix.mjs, the /crm job mode)
   cannot push its own calendar event. It leaves a mark instead: the session
   keeps its gcalEventId but its gcalSyncedFingerprint is null. Nothing in the
   Suite writes that pair on its own — a synced session always carries the
   fingerprint it was synced with — so the pair means exactly "re-push me". */
export const isMarkedForReconcile = (s) => !!s?.date && !!s?.gcalEventId && s?.gcalSyncedFingerprint === null;
export const needsCalendarReconcile = (job) =>
  (Array.isArray(job?.sessions) ? job.sessions : []).some(isMarkedForReconcile);

/* The "previous job" a reconcile diffs against: the job as it is, minus the
   marked sessions. syncJobSessions then finds no previous version of a marked
   session and re-pushes it (notifying attendees, as a schedule change should),
   while every unmarked session compares equal to itself and is skipped — a
   one-day change on a four-day job emails the crew once, not four times. The
   backfill deliberately passes null instead, because it wants every session. */
export const reconcilePrevJob = (job) => ({
  ...job,
  sessions: (Array.isArray(job?.sessions) ? job.sessions : []).filter(s => !isMarkedForReconcile(s)),
});

// Stable string capturing every job/session field that feeds into the Calendar event payload.
// When this key is unchanged between prev and curr, we skip the PATCH entirely.
// `crewCall` is the start the crew call gives the event ("" when it starts at the session
// call) and `startLine` the wording of the line under a moved start ("" without one). The
// current side computes them from the committed cards (eventCrewCall, startLineLabel); the
// previous side reads them from the session's synced fingerprint (syncedCalendarStateOf),
// the record of what the calendar has, so a subject change that changes the words refreshes
// the description (silently: it is not a time change). On the previous side `session` is the
// previous session carrying the schedule its fingerprint records (`before` in syncJobSessions).
function sessionContentKey(job, session, crewCardUrl = "", crewCall = "", startLine = "") {
  return JSON.stringify({
    crewCardUrl: crewCardUrl || "",
    crewCall,
    startLine,
    sessionType: session?.sessionType || "",
    date: session?.date || "",
    callTime: session?.callTime || "",
    wrapTime: session?.wrapTime || "",
    isStudio: !!session?.isStudio,
    locationVenue: session?.locationVenue || "",
    locationAddress: session?.locationAddress || "",
    locationCity: session?.locationCity || "",
    locationState: session?.locationState || "",
    locationZip: session?.locationZip || "",
    notes: session?.notes || "",
    clientName: job?.clientName || "",
    description: job?.description || "",
    equipment: Array.isArray(job?.selectedEquipment) ? [...job.selectedEquipment].sort() : [],
    crew: [...normalizeCrew(job?.crew)].sort(),
  });
}

function buildEventOpts(job, contacts = [], crewRoster = [], ownerEmail = "", jobUrl = "", crewCardUrls = {}) {
  const contact = contacts.find(c => c.id === (job.linkedContacts?.[0]?.contactId));
  const contactName = contact?.name || job.contactName || "";
  const contactPhone = contact?.phones?.[0] || contact?.phone || "";
  const crewNames = normalizeCrew(job.crew);
  const ownerLower = (ownerEmail || "").toLowerCase();
  const attendeeEmails = crewRoster
    .filter(c => crewNames.includes(c.name) && c.email && c.email.toLowerCase() !== ownerLower)
    .map(c => c.email);
  return { contactName, contactPhone, jobUrl, attendeeEmails, crewCardUrls: crewCardUrls || {} };
}

// Fields whose change should fire sendUpdates:"all" (attendee-visible schedule changes).
// A crew call that starts (or stops) moving the event is one: the crew's start moved.
// `prevCrewCall` is the start the calendar has (the synced fingerprint), `currCrewCall` the
// committed card's crew call; `prev` carries the schedule the fingerprint records.
function changedScheduleFields(prev, curr, prevCrewCall = "", currCrewCall = "") {
  const fields = [];
  if ((prev?.date || "") !== (curr?.date || "")) fields.push("date");
  if ((prev?.callTime || "") !== (curr?.callTime || "")) fields.push("callTime");
  if ((prev?.wrapTime || "") !== (curr?.wrapTime || "")) fields.push("wrapTime");
  if (eventCrewCall(prev, prevCrewCall) !== eventCrewCall(curr, currCrewCall)) fields.push("crewCall");
  return fields;
}

/* crewCalls: the committed crew cards' crew calls by session id ("HH:mm"),
   like crewCardUrls; crewSubjects: their subjects by session id ("people" |
   "objects" | "places"; unknown absent), which only decide the wording of the
   line under a moved start. Both are the CURRENT state, read by the current
   session id (the card is keyed by it). The previous state of a session's
   crew call and wording is not a map: it is read from the previous session's
   synced fingerprint (syncedCalendarStateOf), the persisted record of what
   the calendar was last given, which every tab shares through the jobs
   listener; so is the previous schedule (date, call, wrap) when the
   fingerprint has one. A session with no fingerprint (never synced, or
   marked for reconcile) is created, or re-pushed whole, as before. */
export async function syncJobSessions({ prevJob, currJob, integration, contacts, crewRoster, crewCardUrls = {}, crewCalls = {}, crewSubjects = {}, jobUrlBase, callCalendar }) {
  if (!integration?.enabled || !integration.calendarId) {
    return { newSessions: currJob?.sessions || [], errors: [], changed: false };
  }
  const calendarId = integration.calendarId;
  const ownerEmail = integration.ownerEmail || "";
  const prevSessions = prevJob?.sessions || [];
  const currSessions = currJob?.sessions || [];
  const jobUrl = `${jobUrlBase}#/jobs/${currJob.id}`;
  const opts = buildEventOpts(currJob, contacts, crewRoster, ownerEmail, jobUrl, crewCardUrls);

  const prevById = new Map(prevSessions.map(s => [s.id, s]));
  // Secondary index by gcalEventId. When a session id changes but the calendar event persists
  // (e.g. hydrated↔real across a multi-day toggle), looking up prev by eventId lets the
  // content-key check fire and skip no-op updates that would otherwise notify attendees.
  const prevByEventId = new Map(prevSessions.filter(p => p.gcalEventId).map(p => [p.gcalEventId, p]));
  const currIds = new Set(currSessions.map(s => s.id));
  const currEventIds = new Set(currSessions.map(s => s.gcalEventId).filter(Boolean));
  const errors = [];
  let changed = false;

  const deletionTasks = prevSessions
    .filter(s => !currIds.has(s.id) && s.gcalEventId && !currEventIds.has(s.gcalEventId))
    .map(async s => {
      try {
        await callCalendar("delete_event", {
          calendarId, eventId: s.gcalEventId, sendUpdates: sendUpdatesFor([EVENT_DELETED]),
        });
        changed = true;
      } catch (e) {
        errors.push({ sessionId: s.id, op: "delete", reason: e.message });
      }
    });

  const sessionTasks = currSessions.map(async (s) => {
    if (!s.date) return s;
    const crewCardUrl = opts.crewCardUrls[s.id] || "";
    const crewCall = (crewCalls || {})[s.id] || "";
    const crewSubject = (crewSubjects || {})[s.id] || "";
    const moved = eventCrewCall(s, crewCall);
    const event = sessionToEvent(currJob, s, { ...opts, crewCardUrl, crewCall, crewSubject });
    if (!event) return s;
    const currentFingerprint = computeFingerprint(s, crewCall, crewSubject);

    if (!s.gcalEventId) {
      try {
        const { eventId } = await callCalendar("create_event", {
          calendarId, event, sendUpdates: sendUpdatesFor([EVENT_CREATED]),
        });
        changed = true;
        return { ...s, gcalEventId: eventId, gcalSyncedFingerprint: currentFingerprint };
      } catch (e) {
        errors.push({ sessionId: s.id, op: "create", reason: e.message });
        return s;
      }
    }

    const prev = prevById.get(s.id) || (s.gcalEventId ? prevByEventId.get(s.gcalEventId) : null);
    // What the calendar was last given for this session: the previous
    // session's synced fingerprint, never a map this tab keeps. Its schedule
    // (date, call, wrap) is the fingerprint's too, when there is one: the
    // previous job may have been captured before an op ahead in the queue
    // gave the calendar a later schedule (a crew-card sync that ran with the
    // optimistic job), and that schedule is not a change again. Every other
    // field of the previous event comes from prevJob and prev. No fingerprint
    // (never synced, or marked for reconcile): prev as it is; no prev at all
    // re-pushes whole, as before.
    const synced = syncedCalendarStateOf(prev);
    const before = prev && synced.date ? { ...prev, date: synced.date, callTime: synced.callTime, wrapTime: synced.wrapTime } : prev;
    if (before && sessionContentKey(prevJob, before, crewCardUrl, synced.crewCall, synced.startLabel)
      === sessionContentKey(currJob, s, crewCardUrl, moved, moved ? startLineLabel(crewSubject) : "")) {
      return s;
    }
    const scheduleChanged = before ? changedScheduleFields(before, s, synced.crewCall, crewCall) : ["date"];
    try {
      await callCalendar("update_event", {
        calendarId, eventId: s.gcalEventId, event,
        sendUpdates: sendUpdatesFor(scheduleChanged),
      });
      changed = true;
      return { ...s, gcalSyncedFingerprint: currentFingerprint };
    } catch (e) {
      errors.push({ sessionId: s.id, op: "update", reason: e.message });
      return s;
    }
  });

  const [, newSessions] = await Promise.all([
    Promise.all(deletionTasks),
    Promise.all(sessionTasks),
  ]);
  return { newSessions, errors, changed };
}

export async function deleteJobEvents({ job, integration, callCalendar }) {
  if (!integration?.enabled || !integration.calendarId) {
    return { errors: [], deleted: { sessions: [], root: false } };
  }
  const calendarId = integration.calendarId;
  const errors = [];
  const deletedSessions = [];
  let deletedRoot = false;
  // Multi-day jobs store event ids per session. Single-day hydrated jobs store one at the job root.
  const targets = [
    ...(job?.sessions || [])
      .filter(s => s.gcalEventId && !isHydratedSessionId(s.id))
      .map(s => ({ kind: "session", key: s.id, eventId: s.gcalEventId })),
    ...(job?.gcalEventId ? [{ kind: "root", key: job.id, eventId: job.gcalEventId }] : []),
  ];
  await Promise.all(targets.map(async t => {
    try {
      await callCalendar("delete_event", {
        calendarId, eventId: t.eventId, sendUpdates: sendUpdatesFor([EVENT_DELETED]),
      });
      if (t.kind === "root") deletedRoot = true; else deletedSessions.push(t.key);
    } catch (e) {
      errors.push({ sessionId: t.key, op: "delete", reason: e.message });
    }
  }));
  return { errors, deleted: { sessions: deletedSessions, root: deletedRoot } };
}
